import type { CoreEnv } from '@eventbrite/context-gen';
import { track } from '@eventbrite/datalayer-library';
import { DiscoveryEvent } from '@eventbrite/event-renderer';
import { RenderGlobalNavOrUndefined } from '@eventbrite/global-nav';
import {
    getLocaleOverride,
    gettext,
    setLanguage,
    setup as setupI18n,
} from '@eventbrite/i18n';
import { SiteStructure } from '@eventbrite/site-structure';
import { logEvent } from '@eventbrite/statsig';
import { UrgencySignalsProps } from '@eventbrite/urgency-signals';
import { ErrorBoundary } from '@sentry/react';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { App } from './app';
import { ArtistWidgetWrapper as ArtistWidget } from './components/ArtistWidget/ArtistWidget';
import type { ArtistWidget as ArtistWidgetType } from './components/ArtistWidget/types';
import { CreatorBanner } from './components/CreatorBanner/CreatorBanner';
import { CreatorCollections } from './components/EventDetails/CreatorCollections/CreatorCollections';
import {
    OldDescriptionBeforeStructuredContent,
    Overview,
    type EventDescriptionProps,
} from './components/EventDetails/EventDescription';
import {
    EventDetails,
    EventDetailsWrapper,
} from './components/EventDetails/EventDetails';
import { EventHero } from './components/EventDetails/EventHero';
import { MusicEventsInfo } from './components/EventDetails/MusicEventsInfo/MusicEventsInfo';
import { Performers } from './components/EventDetails/Performers/Performers';
import { ViewMoreDetails } from './components/EventDetails/ViewMoreDetails/ViewMoreDetails';

import { SalesEndedMessage } from './components/EventDetails/SalesEndedMessage/SalesEndedMessage';
import StartDateAndLocation from './components/EventDetails/StartDateAndLocation';

import { DateAndTime } from './components/EventDetails/WhenAndWhere/DateAndTime/DateAndTime';
import { WhenAndWhere } from './components/EventDetails/WhenAndWhere/WhenAndWhere';
import { AgendaWidget } from './components/EventDetails/Widgets/NewAgenda/types';
import { parseWidgets } from './components/EventDetails/Widgets/utils';
import { EventManagerBanner } from './components/EventManagerBanner/EventManagerBanner';
import { EnhancedExpiredEventsList } from './components/ExpiredEvents/EnhancedExpiredEventsList';
import { ExpiredEventsPage } from './components/ExpiredEvents/ExpiredEventsPage';
import type { FooterLinks } from './components/Footer/Footer';
import { Footer } from './components/Footer/Footer';
import { GoodToKnow } from './components/GoodToKnow/GoodToKnow';
import {
    AsideModule,
    Layout,
    LayoutModule,
    MainContentModule,
    Modules,
} from './components/Layout';
import MaintenanceBanner from './components/MaintenanceBanner/MaintenanceBanner';
import type { NotificationBarSsrProps } from './components/NotificationBar/NotificationBarSsr';
import { NotificationBarSsr } from './components/NotificationBar/NotificationBarSsr';
import { PageTitle } from './components/PageTitle/PageTitle';
import {
    getShareComponentProps,
    ShareProps,
} from './components/ShareEvent/ShareEventRoot';
import type { Tag } from './components/Tags/Tags';
import {
    TagsForExpiredEvents,
    TagsForLiveEvents,
} from './components/Tags/Tags';
import { TestAaSsr } from './components/TestAAServerSideRendering/TestAaSsr';
import type { HighlightsProps } from './types/highlights';
import { type OrganizerPanelLayoutProps } from './types/organizerPanel';

import '@eventbrite/marmalade-design-tokens/web/tokens.css';
import { AgendaSection } from './components/Agenda/AgendaSection';
import { ConversionBarContainer } from './components/EventDetails/ConversionBarContainer/ConversionBarContainer';
import { LocationProps } from './components/EventDetails/WhenAndWhere/Location/Location';
import { InPersonMap } from './components/EventDetails/WhenAndWhere/Location/types';
import { FaqWidget } from './components/EventDetails/Widgets/Faq';
import { ExtraInfoAndActionBar } from './components/ExtraInfoAndActionBar/ExtraInfoAndActionBar';
import { FAQSection } from './components/FAQ/FAQSection';
import { MoreEventsSection } from './components/MoreEvents/MoreEventsSection';
import { NewOrganizerPanel } from './components/NewOrganizerPanel/NewOrganizerPanel';
import { ReportThisEventButton } from './components/ReportThisEvent/ReportThisEventButton';
import { IsLtc } from './components/utils/IsLtc';
import {
    FollowButtonGDPRModal,
    FollowButtonLoginModal,
    FollowButtonProvider,
} from './components/_shared/FollowButton';
import OrganiserListingPage from './components/_shared/OrganizerInfo/OrganiserListingPage/OrganiserListingPage';
import { useEventBasicInformationContext } from './contexts';
import { useListingTimedEntryExperimentContext } from './contexts/ListingTimedEntryExperimentContext';
import {
    isDetachInterestsExperimentEnabled,
    isTESessionSelectorExperimentEnabled,
    STATSIG_EVENT_NAMES,
} from './experimentation';
import { ListingsProviders } from './providers';
import './styles/root.scss';
import type {
    AppProps,
    CheckoutWidgetProps,
    Discount,
    Event,
    EventDetailsProps,
    EventHeroScopedProps,
    EventListingsFeatureFlags,
    EventMapScopedProps,
    Experimentation,
    FollowOrganizerScopedProps,
    LegacyConversionBarProps,
    NativeVideoProps,
    Organizer,
    RemindMeScopedProps,
    Request,
    Settings,
    StatsigProps,
    TicketsProps,
    TimedEntryEventProps,
    User,
} from './types';
import { AffiliateCodes, SALES_STATUS } from './types';
import {
    getSalesStatusCode,
    isDraftInPreview,
    isRepeatingEvent,
    shouldShowForDraftInPreview,
} from './utils';
import {
    getDiscountFromEventListings,
    hasBogoTickets,
    hasEarlyBirdTickets,
} from './utils/discounts';

const FEATURE_FLAGS = {
    launchCreatorCreditsSectionAttendee: true,
};

export interface ServerAppProps {
    checkoutWidget: CheckoutWidgetProps;
    conversionBar: LegacyConversionBarProps;
    user: User;
    event: Event;
    env: CoreEnv;
    request: Request;
    footerLinks: FooterLinks;
    eventDescription: EventDescriptionProps;
    eventDetails: EventDetailsProps;
    organizer: Organizer;
    tags: Array<Tag>;
    settings: Settings;
    organizerPanelLayout: OrganizerPanelLayoutProps;
    eventMapLayout: EventMapScopedProps;
    eventHero: EventHeroScopedProps;
    notificationBar: NotificationBarSsrProps;
    shareComponentProps: ShareProps;
    expiredEventsPage: {
        relatedEvents: DiscoveryEvent[];
    };
    urgencySignals: UrgencySignalsProps;
    highlights: HighlightsProps;
    followOrganizer: FollowOrganizerScopedProps;
    contactOrganizer: {
        captchaKey: string;
    };
    remindMe: RemindMeScopedProps;
    gaCategory: string;
    gaSettings: any;
    salesStatus: string;
    appProps: AppProps;
    statsig: StatsigProps;
    tickets: TicketsProps;
    featureFlags: EventListingsFeatureFlags;
    eventSessionResponse: Array<TimedEntryEventProps>;
    nativeVideo?: NativeVideoProps;
    parentEventId?: string;
    discount?: Discount;
    experimentation?: Experimentation;
}

export const ServerApp = ({
    checkoutWidget,
    conversionBar,
    user,
    event,
    env,
    request,
    footerLinks,
    eventDescription,
    eventDetails,
    organizer,
    tags,
    settings,
    organizerPanelLayout,
    eventMapLayout,
    eventHero,
    notificationBar = {
        message: null,
        type: null,
    },
    shareComponentProps,
    expiredEventsPage,
    urgencySignals,
    highlights,
    followOrganizer,
    contactOrganizer: { captchaKey },
    remindMe,
    gaCategory,
    gaSettings,
    appProps,
    statsig,
    tickets,
    featureFlags,
    eventSessionResponse,
    nativeVideo,
    parentEventId,
    discount,
    salesStatus,
    experimentation,
}: ServerAppProps) => {
    const {
        hasCroppedLogo,
        organizerSalesEndedMessage,
        presenter,
        salesEndedMessage,
        shouldShowSalesEndedMessage,
        isOnlineEvent,
        hideStartDate,
        hideEndDate,
    } = event;

    const {
        hasStructuredContent,
        structuredContent,
        summary,
        legacyDescription,
    } = eventDescription;

    const {
        id: organizerId,
        name: organizerName,
        profilePicture: organizerAvatar,
    } = organizer;

    const {
        featureFlags: {
            enableIaGlobalNav = false,
            launchLinkToCreatorCollectionsOnListing = false,
            isOnlineEventMvpEnabled = false,
        } = {},
    } = settings;

    const {
        localeInfo: { locale, help_center_language: helpCenterLanguage },
    } = env;
    const enableTimedEntry = featureFlags?.enableTimedEntry ?? false;
    // https://eventbrite.atlassian.net/browse/EB-279367
    const onlyDefaultCheckoutForOnlineEvents =
        isOnlineEvent &&
        featureFlags?.shouldShowOnlyDefaultCheckoutOnlineEvents;

    const { performers, dates } = eventDetails;
    const hasHeadliners =
        Array.isArray(performers.headliners) &&
        performers.headliners.length > 0;
    const hasSupporters =
        Array.isArray(performers.supporters) &&
        performers.supporters.length > 0;
    const hasPerformers = hasHeadliners || hasSupporters;
    const enableArtistWidget = featureFlags?.enableArtistWidget ?? false;

    const isSalesEnded = salesStatus === SALES_STATUS.SALES_ENDED;
    const isSoldOut = salesStatus === SALES_STATUS.SOLD_OUT;

    const refunds = appProps['event_listing_response']?.refunds;

    const forceViewDetails = request.query_params?.viewDetails === 'true';

    const LiveEventMainSection = ({
        onViewAllDetailsClick,
        viewAllEventDetails,
    }: {
        onViewAllDetailsClick: Function;
        viewAllEventDetails: boolean;
    }) => {
        const { isSeriesParent, salesStatus: eventBasicInfoSalesStatus } =
            useEventBasicInformationContext();
        const { isListingTimedEntryExperimentActive } =
            useListingTimedEntryExperimentContext();

        const eventBasicInfoIsSalesEnded =
            eventBasicInfoSalesStatus === SALES_STATUS.SALES_ENDED;
        const eventBasicInfoIsSoldOut =
            eventBasicInfoSalesStatus === SALES_STATUS.SOLD_OUT;

        const seriesInfiniteCalendarSplitFF =
            settings.featureFlags?.seriesInfiniteCalendar || false;

        const isRepeating = isRepeatingEvent({
            event,
            event_listing_response: appProps['event_listing_response'],
        });

        const shouldShowDateAndTimeForOldTimedEntry =
            !seriesInfiniteCalendarSplitFF && isRepeating;

        const showNewTimedEntryExperienceSignalCondition =
            isListingTimedEntryExperimentActive &&
            isSeriesParent == false &&
            !(eventBasicInfoIsSoldOut || eventBasicInfoIsSalesEnded);

        const salesStatusMessage =
            event.salesStatus.message ||
            (eventBasicInfoIsSoldOut && 'Sold Out') ||
            (eventBasicInfoIsSalesEnded && 'Sales Ended') ||
            '';

        const [isLoadingSignal, setIsLoadingSignal] = useState(true);

        useEffect(() => {
            setIsLoadingSignal(false);
        }, []);

        useEffect(() => {
            if (isListingTimedEntryExperimentActive) {
                track({
                    eventName: 'listing_timed_entry_experiment_loaded',
                    eventData: {},
                });
            }
        }, [isListingTimedEntryExperimentActive]);

        const {
            agenda: agendaWidget,
            faqs: faqWidget,
            lineup: artistWidget,
        } = parseWidgets(structuredContent.widgets);

        const location: LocationProps = {
            ...eventDetails.location,
            isOnlineEvent: event.isOnlineEvent,
            onlineEventUrl: eventDetails.onlineEvent?.url,
        } as LocationProps;

        const map: InPersonMap = {
            location: eventMapLayout.location,
            gMapsApiKey: eventMapLayout.gMapsApiKey,
            shouldDisplayMap: eventMapLayout.shouldDisplayMap,
        } as InPersonMap;

        const shouldShowExpiredEventsBadge =
            !isLoadingSignal &&
            shouldShowForDraftInPreview(event.isPreview, event.isLive) &&
            (eventBasicInfoIsSalesEnded || eventBasicInfoIsSoldOut) &&
            !showNewTimedEntryExperienceSignalCondition;

        return (
            <div
                className={`event-listing ${
                    hasCroppedLogo ? 'event-listing--has-image' : ''
                }`}
                role="article"
                data-testid="live-event-main-section"
            >
                <TestAaSsr />
                {(!eventBasicInfoIsSalesEnded || !eventBasicInfoIsSoldOut) &&
                    notificationBar.message &&
                    !isListingTimedEntryExperimentActive &&
                    !seriesInfiniteCalendarSplitFF && (
                        <NotificationBarSsr {...notificationBar} />
                    )}
                {user.canManageEvent &&
                    !settings.featureFlags
                        ?.launchMobilePreviewInEventCreationFlow && (
                        <CreatorBanner />
                    )}
                <EventDetails
                    hasHeroSection={hasCroppedLogo || !!eventHero.featuredVideo}
                >
                    <EventHero
                        mainBodyBackground={eventHero.mainBodyBackground}
                        hasCroppedLogo={hasCroppedLogo}
                        items={eventHero.items}
                        eventName={event.name}
                        featuredVideo={eventHero.featuredVideo}
                        nativeVideo={nativeVideo}
                    />
                    <MaintenanceBanner />
                    <ExtraInfoAndActionBar
                        shouldShowExpiredEventsBadge={
                            shouldShowExpiredEventsBadge
                        }
                        urgencySignals={urgencySignals}
                        salesMessage={salesStatusMessage}
                        salesMessageCode={getSalesStatusCode(event)}
                        isShareable={
                            conversionBar.isShareable && !conversionBar.isEnded
                        }
                        isExpiredEvent={
                            eventBasicInfoIsSalesEnded ||
                            eventBasicInfoIsSoldOut
                        }
                    />
                    <EventDetailsWrapper>
                        <Layout
                            exploreMoreEvents={
                                shouldShowForDraftInPreview(
                                    event.isPreview,
                                    event.isLive,
                                ) &&
                                (eventBasicInfoIsSalesEnded ||
                                    eventBasicInfoIsSoldOut) &&
                                !viewAllEventDetails
                            }
                        >
                            <MainContentModule>
                                <LayoutModule
                                    name={Modules.salesEndedMessage}
                                    noDivider
                                >
                                    {shouldShowSalesEndedMessage && (
                                        <SalesEndedMessage
                                            organizerSalesEndedMessage={
                                                organizerSalesEndedMessage
                                            }
                                            salesEndedMessage={
                                                salesEndedMessage
                                            }
                                        />
                                    )}
                                </LayoutModule>

                                <LayoutModule name={Modules.mainInfo}>
                                    <LayoutModule
                                        name={Modules.title}
                                        noDivider
                                    >
                                        <meta content={event.name} />
                                        <PageTitle
                                            pageTitle={event.name}
                                            hideStartDate={hideStartDate}
                                        />
                                    </LayoutModule>

                                    <LayoutModule
                                        name={Modules.organizerBrief}
                                        noDivider
                                    >
                                        {organizer.displayOrganizationName && (
                                            <OrganiserListingPage
                                                featureFlags={
                                                    settings?.featureFlags
                                                }
                                                isMobile={env.isMobile}
                                            />
                                        )}
                                    </LayoutModule>

                                    <LayoutModule
                                        name={Modules.startDateAndLocation}
                                        noDivider
                                    >
                                        <StartDateAndLocation
                                            isOnlineEvent={isOnlineEvent}
                                            isSeriesParent={isSeriesParent}
                                            isRepeating={isRepeating}
                                            hideStartDate={hideStartDate}
                                            locale={locale}
                                            location={location}
                                        />
                                    </LayoutModule>

                                    <LayoutModule
                                        name={Modules.musicEventInfo}
                                        noDivider
                                    >
                                        {!!presenter && (
                                            <MusicEventsInfo
                                                presenter={presenter}
                                            />
                                        )}
                                    </LayoutModule>

                                    <LayoutModule
                                        name={Modules.creatorCollections}
                                        noDivider
                                    >
                                        <CreatorCollections />
                                    </LayoutModule>
                                </LayoutModule>
                                {shouldShowDateAndTimeForOldTimedEntry && (
                                    <LayoutModule name={Modules.dateAndTime}>
                                        <DateAndTime
                                            {...{
                                                ...eventDetails.dates,
                                                event: {
                                                    start: event.start,
                                                    end: event.end,
                                                    hideEndDate:
                                                        event.hideEndDate,
                                                    hideStartDate:
                                                        event.hideStartDate,
                                                    isOnlineEvent:
                                                        event.isOnlineEvent,
                                                    childEvents:
                                                        event.childEvents,
                                                    listingDisplayFlags: {
                                                        shouldShowTimezone:
                                                            true,
                                                    },
                                                },
                                                locale,
                                                isTimedEntryEnabled:
                                                    enableTimedEntry,
                                                isTESessionSelectorExperimentEnabled:
                                                    isTESessionSelectorExperimentEnabled(
                                                        eventSessionResponse?.length ||
                                                            0,
                                                        eventBasicInfoIsSalesEnded,
                                                        eventBasicInfoIsSoldOut,
                                                        event.shouldShowCompactCheckout,
                                                    ),
                                            }}
                                        />
                                    </LayoutModule>
                                )}

                                <LayoutModule name={Modules.overview}>
                                    {hasStructuredContent ||
                                    !legacyDescription ? (
                                        <Overview
                                            hasAutogeneratedContent={
                                                structuredContent.hasAutogeneratedContent
                                            }
                                            modules={structuredContent.modules}
                                            summary={summary}
                                            category={event.category}
                                            subcategory={event.subcategory}
                                            hideEndDate={event.hideEndDate}
                                            isMobile={env.isMobile}
                                        />
                                    ) : (
                                        <OldDescriptionBeforeStructuredContent
                                            legacyDescription={
                                                legacyDescription
                                            }
                                        />
                                    )}
                                </LayoutModule>

                                <LayoutModule name={Modules.performers}>
                                    {hasPerformers &&
                                        (!enableArtistWidget ||
                                            !artistWidget) && (
                                            <Performers
                                                headliners={
                                                    eventDetails.performers
                                                        .headliners
                                                }
                                                supporters={
                                                    eventDetails.performers
                                                        .supporters
                                                }
                                            />
                                        )}
                                </LayoutModule>

                                <LayoutModule name={Modules.artistWidget}>
                                    {enableArtistWidget && artistWidget && (
                                        <ArtistWidget
                                            eventId={event.id}
                                            artistData={
                                                artistWidget as ArtistWidgetType
                                            }
                                        />
                                    )}
                                </LayoutModule>

                                <LayoutModule name={Modules.goodToKnow}>
                                    <GoodToKnow
                                        highlights={{
                                            ...highlights,
                                            duration: !(
                                                hideEndDate || hideStartDate
                                            )
                                                ? highlights.duration
                                                : undefined,
                                        }}
                                        isOnline={event.isOnlineEvent}
                                        doorTime={dates.doorTimeLineThree}
                                        ageRestriction={event.ageRestriction}
                                        refundPolicy={refunds}
                                        location={location}
                                    />
                                </LayoutModule>

                                <LayoutModule name={Modules.location}>
                                    <WhenAndWhere
                                        location={location}
                                        map={map}
                                    />
                                </LayoutModule>

                                <LayoutModule name={Modules.agenda}>
                                    <AgendaSection
                                        agenda={
                                            agendaWidget as
                                                | AgendaWidget
                                                | undefined
                                        }
                                    />
                                </LayoutModule>

                                <LayoutModule name={Modules.faq} noDivider>
                                    <FAQSection
                                        widget={faqWidget as FaqWidget}
                                    />
                                </LayoutModule>

                                <LayoutModule
                                    name={Modules.organizerPanel}
                                    noDivider
                                >
                                    <NewOrganizerPanel
                                        captchaKey={captchaKey}
                                        gaCategory={gaCategory}
                                        isAuthenticated={user.isAuthenticated}
                                        isMobile={env.isMobile}
                                    />
                                </LayoutModule>

                                <LayoutModule name={Modules.reportEvent}>
                                    <ReportThisEventButton
                                        country={event?.venue?.country}
                                        helpCenterLanguage={helpCenterLanguage}
                                    />
                                </LayoutModule>

                                <LayoutModule
                                    name={Modules.relatedEvents}
                                    noDivider
                                >
                                    <MoreEventsSection
                                        shouldShowMoreEventsFromThisOrganizer={
                                            organizerPanelLayout.shouldShowMoreEventsFromThisOrganizer ??
                                            false
                                        }
                                        isEventActive={
                                            !eventBasicInfoIsSalesEnded ||
                                            !eventBasicInfoIsSoldOut
                                        }
                                    />
                                </LayoutModule>

                                <LayoutModule name={Modules.tags}>
                                    {
                                        <TagsForExpiredEvents
                                            tags={tags}
                                            isSoldOut={eventBasicInfoIsSoldOut}
                                            isSalesEnded={
                                                eventBasicInfoIsSalesEnded
                                            }
                                        />
                                    }
                                </LayoutModule>

                                <LayoutModule
                                    name={Modules.enhancedExpiredEvents}
                                >
                                    {(eventBasicInfoIsSalesEnded ||
                                        eventBasicInfoIsSoldOut) && (
                                        <>
                                            {!viewAllEventDetails && (
                                                <ViewMoreDetails
                                                    onViewAllDetailsClick={
                                                        onViewAllDetailsClick
                                                    }
                                                />
                                            )}

                                            <EnhancedExpiredEventsList
                                                {...{
                                                    isAuthenticated:
                                                        user.isAuthenticated,
                                                    locale,
                                                    affCode:
                                                        AffiliateCodes.AFF_RELATED_EVENTS_EXPIRED,
                                                    organizer,
                                                    launchLinkToCreatorCollectionsOnListing,
                                                    isExpiredEvent:
                                                        event.isExpiredEvent,
                                                    forceTwoColumnLayout: true,
                                                    eventBasicInfoIsSoldOut,
                                                    eventBasicInfoIsSalesEnded,
                                                }}
                                            />
                                        </>
                                    )}
                                </LayoutModule>

                                <LayoutModule name={Modules.tags} noDivider>
                                    <TagsForLiveEvents
                                        tags={tags}
                                        isSoldOut={eventBasicInfoIsSoldOut}
                                        isSalesEnded={
                                            eventBasicInfoIsSalesEnded
                                        }
                                    />
                                </LayoutModule>
                            </MainContentModule>
                            <AsideModule>
                                <LayoutModule
                                    name={Modules.conversionBar}
                                    noDivider
                                >
                                    <ConversionBarContainer
                                        {...{
                                            ...conversionBar,
                                            ...checkoutWidget,
                                            externalTickets: {
                                                url: conversionBar.externalTicketsUrl,
                                                text: conversionBar.externalTicketsButtonText,
                                                available:
                                                    conversionBar.externalTicketsAreAvailable,
                                                shouldShowExternalTicketingProvider:
                                                    conversionBar.shouldShowExternalTicketingProvider,
                                                externalTicketingProviderLabelText:
                                                    conversionBar.externalTicketingProviderLabelText,
                                            },
                                            childEvents: event.childEvents,
                                            isExpiredEvent:
                                                event.isExpiredEvent,
                                            isPreview: event.isPreview,
                                            isLive: event.isLive,
                                            isFree:
                                                tickets?.availability?.isFree ??
                                                false,
                                            eventName: event.name,
                                            eventId: event.id,
                                            ticketsInfo: event.ticketsInfo,
                                            compactCheckoutDisqualifications:
                                                event.compactCheckoutDisqualifications,
                                            salesStatus: isDraftInPreview(
                                                event.isPreview,
                                                event.isLive,
                                            )
                                                ? 'on_sale'
                                                : event.salesStatus
                                                      .sales_status,
                                            gaCategory,
                                            gaSettings,
                                            isAuthenticated:
                                                user.isAuthenticated,
                                            isGDPRCountry:
                                                followOrganizer.isGDPRCountry,
                                            isRemindedEvent:
                                                remindMe?.isRemindedEvent,
                                            organizerId,
                                            organizerData: {},
                                            organizers: {},
                                            userId: user.id,
                                            organizerName: organizer.name,
                                            organizerAvatar:
                                                organizer.profilePicture,
                                            isSalesEnded:
                                                eventBasicInfoIsSalesEnded,
                                            isSoldOut: eventBasicInfoIsSoldOut,
                                            hasDonationTicketsAvailable:
                                                event?.hasDonationTicketsAvailable ??
                                                false,
                                            hasWaitlist:
                                                event
                                                    .compactCheckoutDisqualifications
                                                    .has_waitlist,
                                            hasAvailableHiddenTickets:
                                                tickets?.availability
                                                    ?.hasAvailableHiddenTickets ??
                                                false,
                                            waitlistAvailable:
                                                tickets?.availability
                                                    ?.waitlistAvailable,
                                            waitlistEnabled:
                                                tickets?.availability
                                                    ?.waitlistEnabled,
                                            onlyDefaultCheckoutForOnlineEvents:
                                                onlyDefaultCheckoutForOnlineEvents,
                                            isOnlineEventMvpEnabled:
                                                isOnlineEventMvpEnabled,
                                            isTESessionSelectorExperimentEnabled:
                                                isTESessionSelectorExperimentEnabled(
                                                    eventSessionResponse?.length ||
                                                        0,
                                                    eventBasicInfoIsSalesEnded,
                                                    eventBasicInfoIsSoldOut,
                                                    event.shouldShowCompactCheckout,
                                                ),
                                            eventStartDate: event.start,
                                            eventEndDate: event.end,
                                        }}
                                    />
                                </LayoutModule>
                            </AsideModule>
                        </Layout>
                    </EventDetailsWrapper>
                </EventDetails>

                {shouldShowRelatedEventsLive &&
                    (!eventBasicInfoIsSalesEnded ||
                        !eventBasicInfoIsSoldOut) && (
                        <div id="listings-root__related-events"></div>
                    )}
                <EventManagerBanner canManage={user.canManageEvent || false} />
            </div>
        );
    };
    const MainContent = () => {
        const [shouldHideMainBody, setHideMainBody] = useState(
            !event.isPreview && event.isExpiredEvent && !forceViewDetails,
        );
        const [viewAllEventDetails, setViewAllEventDetails] =
            useState(forceViewDetails);

        const showEventMainSection = () => {
            setHideMainBody(false);
        };

        const showAllEventDetails = () => {
            setHideMainBody(false);
            setViewAllEventDetails(true);
            logEvent(STATSIG_EVENT_NAMES.LISTING_ALL_EVENT_DETAILS_VIEW);
        };

        return (
            <ListingsProviders
                event={{
                    ...event,
                    ticketsInfo: {
                        ...event.ticketsInfo,
                        // TODO: This is a temporal solution, once we tackle EB-233327 we will simply take this field from SERVER_DATA
                        hasEarlyBirdTickets:
                            urgencySignals?.categories?.earlyBird ?? false,
                    },
                }}
                eventDescription={eventDescription}
                eventMapLayout={eventMapLayout}
                eventDetails={eventDetails}
                user={user}
                settings={settings}
                env={env}
                request={request}
                statsig={statsig}
                conversionBar={conversionBar}
                tickets={tickets}
                urgencySignals={urgencySignals}
                eventSessionResponse={eventSessionResponse}
                parentEventId={parentEventId}
                featureFlags={featureFlags}
                discount={discount}
                experimentation={experimentation}
                organizer={organizer}
            >
                {!shouldHideMainBody && (
                    <LiveEventMainSection
                        onViewAllDetailsClick={showAllEventDetails}
                        viewAllEventDetails={viewAllEventDetails}
                    />
                )}
                {shouldHideMainBody && (
                    <ExpiredEventsPage
                        relatedEvents={expiredEventsPage.relatedEvents}
                        isAuthenticated={user.isAuthenticated}
                        shareComponentProps={shareComponentProps}
                        onViewDetailsClick={showEventMainSection}
                        locale={locale}
                        isSalesEnded={isSalesEnded}
                        isSoldOut={isSoldOut}
                        salesMessage={event.salesStatus.message}
                        salesMessageCode={getSalesStatusCode(event)}
                    />
                )}
                <IsLtc />
                <App {...appProps} />
            </ListingsProviders>
        );
    };

    const shouldShowRelatedEventsLive = event.isLive;

    const cleanEsiCachePaths = (env: CoreEnv) => {
        const esiPath = user.isAuthenticated
            ? /%2Fesi_nocache/g
            : /%2Fesi_cache/g;
        const removeEsiPath = (string: string): string =>
            string ? string.replace(esiPath, '') : '';

        env.loginUrl = removeEsiPath(env.loginUrl);
        env.logoutUrl = removeEsiPath(env.logoutUrl);
        env.signinUrl = removeEsiPath(env.signinUrl);
        env.signupUrl = removeEsiPath(env.signupUrl);
    };

    cleanEsiCachePaths(env);

    const siteWrapperClasses = classNames({
        'conversion-bar-placeholder': !event.isExpiredEvent,
    });

    return (
        <ErrorBoundary fallback={<>{gettext('Error on Listings SSR')}</>}>
            <div className={siteWrapperClasses}>
                <SiteStructure
                    env={env}
                    request={request}
                    user={user}
                    preview={event.isPreview}
                    featureFlags={{
                        ...FEATURE_FLAGS,
                        isDetachInterestsEnabled:
                            isDetachInterestsExperimentEnabled(),
                    }}
                    fullScreenMain={true}
                    globalHeader={RenderGlobalNavOrUndefined({
                        config: { env, user },
                        featureFlag: enableIaGlobalNav,
                    })}
                    footer={
                        <Footer {...footerLinks} serverUrl={env.serverUrl} />
                    }
                    removeOverflowSettings={true}
                    focusDrawerOptions={{
                        content: '',
                        alwaysInclude: false,
                    }}
                >
                    <FollowButtonProvider
                        organizerId={organizerId}
                        userId={user.id}
                        isGDPRCountry={followOrganizer.isGDPRCountry}
                        isAuthenticated={user.isAuthenticated}
                        event={event}
                    >
                        <MainContent />
                        <FollowButtonGDPRModal />
                        <FollowButtonLoginModal
                            organizerId={organizerId}
                            organizerName={organizerName}
                            organizerAvatar={organizerAvatar}
                        />
                    </FollowButtonProvider>
                </SiteStructure>
            </div>
        </ErrorBoundary>
    );
};

export const render = (props: AppProps) => {
    const locale = getLocaleOverride() || props.env.localeInfo.locale;
    setupI18n(require.context('./i18n/translations', true, /\.json$/));
    setLanguage(locale);

    return renderToString(
        <ServerApp
            user={props.user}
            event={{
                ...props.event,
                isRepeating: isRepeatingEvent(props),
                ticketsInfo: {
                    ...props.event.ticketsInfo,
                    hasEarlyBirdTickets: hasEarlyBirdTickets(
                        props['event_listing_response'].tickets,
                    ),
                    hasBogoTickets: hasBogoTickets(
                        props['event_listing_response'].tickets,
                    ),
                },
            }}
            request={props.request}
            env={props.env}
            footerLinks={props.footerLinks}
            eventDescription={{ ...props.components.eventDescription }}
            eventDetails={{ ...props.components.eventDetails }}
            tags={props.components.tags}
            conversionBar={props.components.conversionBar}
            checkoutWidget={props.components.checkoutWidget}
            settings={props.settings}
            organizerPanelLayout={props.components.organizerPanel}
            eventMapLayout={props.components.eventMap}
            eventHero={props.components.eventHero}
            organizer={props.organizer}
            notificationBar={props.components.notificationBar}
            expiredEventsPage={props.components.relatedEventsExpiredPage}
            shareComponentProps={getShareComponentProps(props)}
            urgencySignals={props.urgencySignals}
            highlights={{
                ...props.highlights,
                ...props['event_listing_response'].highlights,
            }}
            gaCategory={props.gaSettings.category}
            gaSettings={props.gaSettings}
            followOrganizer={props.components.followOrganizer}
            contactOrganizer={props.components.contactOrganizer}
            remindMe={props.components.remindMe}
            salesStatus={props.salesStatus}
            appProps={props}
            statsig={props.statsig}
            tickets={props['event_listing_response'].tickets}
            featureFlags={props['event_listing_response'].featureFlags}
            eventSessionResponse={props['event_listing_response'].eventSessions}
            nativeVideo={props['event_listing_response'].video}
            parentEventId={props['event_listing_response']?.parentEventId}
            discount={getDiscountFromEventListings(
                props.env.localeInfo.locale,
                props['event_listing_response'].tickets,
            )}
        />,
    );
};

export default ServerApp;
